import React, { useState } from 'react';
import { ActivityIndicator, StyleProp, TextStyle, TouchableOpacity, View, ViewStyle } from 'react-native';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { Hoverable } from 'react-native-web-hover';
import MyAppText from './MyAppText';
import { WindowInformation } from '../services/window-information';
import FontWeight from '../styles/fontWeights';

export type ButtonTypes = 'primary' | 'secondary' | 'tertiary' | 'destructive' | 'warning' | 'destructive_secondary';

interface Props {
    icon?: IconDefinition;
    text?: string;
    onPress: () => (Promise<unknown> | unknown);
    /**
     * button style
     */
    style?: StyleProp<ViewStyle>;
    /**
     * text style
     */
    textStyle?: TextStyle;
    /**
     * button style
     */
    contentContainerStyle?: StyleProp<ViewStyle>;
    isLoading?: boolean;
    disabled?: boolean;
    isActive?: boolean;
    type?: ButtonTypes;
}

function CustomButton({ icon, text, onPress, style, textStyle, isLoading = false, disabled = false, type = 'primary', contentContainerStyle, isActive = false }: Props) {
    const { styles } = useStyles(stylesheet);

    const [internalIsLoading, setInternalIsLoading] = useState(false);
    const windowInfo = WindowInformation();

    function buttonContent(hovered: boolean, disabled: boolean) {
        if (isLoading || internalIsLoading) {
            return <ActivityIndicator size='small' color={styles.loadingColor.color} />;
        }
        return (
            <React.Fragment>
                {icon && <FontAwesomeIcon style={styles.buttonIcon(type, hovered, disabled, textStyle)} fontSize={textStyle?.fontSize || 16} icon={icon} />}
                {text && <MyAppText style={[styles.buttonText(type, hovered, disabled, windowInfo.isMobile), textStyle]}>{text}</MyAppText>}
            </React.Fragment>
        );
    }

    return (
        <TouchableOpacity
            style={contentContainerStyle}
            onPress={async () => {
                setInternalIsLoading(true);
                const res = onPress();
                await res;
                setInternalIsLoading(false);
            }}
            disabled={disabled || isLoading || internalIsLoading}
        >
            <Hoverable>
                {({ hovered }) => (
                    <View style={[styles.buttonContainer(type, hovered, disabled || isLoading || internalIsLoading, isActive, windowInfo.isMobile), style]}>
                        {buttonContent(hovered, disabled)}
                    </View>
                )}
            </Hoverable>
        </TouchableOpacity>
    );
}

const stylesheet = createStyleSheet(theme => ({
    buttonContainer: (type: ButtonTypes, hovered: boolean, disabled: boolean, isActive: boolean, isMobile: boolean) => {

        let backgroundColor: string | undefined;
        let borderColor: string | undefined;
        let borderWidth = 0;

        if (type == 'primary') {
            backgroundColor = disabled ? theme.colors.primaryButton.disabledBackground : hovered ? theme.colors.primaryButton.hoverBackground : theme.colors.primaryButton.background;
            borderWidth = 0;
            borderColor = undefined;
        } else if (type == 'secondary') {
            backgroundColor = isActive ? theme.colors.secondaryButton.activeBackgroundColor : disabled ? theme.colors.secondaryButton.disabledBackground : hovered ? theme.colors.secondaryButton.hoverBackground : theme.colors.secondaryButton.background;
            borderWidth = 1;
            borderColor = disabled ? theme.colors.secondaryButton.disabledBorderColor : hovered ? theme.colors.secondaryButton.hoverBorderColor : theme.colors.secondaryButton.borderColor;
        } else if (type == 'tertiary') {
            backgroundColor = disabled ? 'transparent' : hovered ? theme.colors.backgrounds['BG-1'] : 'transparent';
            borderWidth = 0;
            borderColor = undefined;
        } else if (type == 'destructive') {
            backgroundColor = disabled ? theme.colors.destructiveButton.disabledBackground : hovered ? theme.colors.destructiveButton.hoverBackground : theme.colors.destructiveButton.background;
            borderWidth = 0;
            borderColor = undefined;
        } else if (type == 'warning') {
            backgroundColor = disabled ? theme.colors.warningButton.disabledBackground : hovered ? theme.colors.warningButton.hoverBackground : theme.colors.warningButton.background;
            borderWidth = 0;
            borderColor = undefined;
        } else if (type == 'destructive_secondary') {
            backgroundColor = isActive ? theme.colors.destructiveSecondaryButton.activeBackgroundColor : disabled ? theme.colors.destructiveSecondaryButton.disabledBackground : hovered ? theme.colors.destructiveSecondaryButton.background : theme.colors.destructiveSecondaryButton.background;
            borderWidth = 1;
            borderColor = hovered && !disabled ? theme.colors.destructiveSecondaryButton.hoverBorderColor : 'transparent';
        }

        return {
            flexDirection: 'row',
            alignItems: 'center',
            borderRadius: 5,
            borderWidth: borderWidth,
            borderColor: borderColor,
            paddingHorizontal: 12,
            width: '100%',
            columnGap: 10,
            height: isMobile ? 56 : 40,
            backgroundColor: backgroundColor,
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            justifyContent: 'center',
            gap: 10
        };
    },

    buttonText: (type: ButtonTypes, hovered: boolean, disabled: boolean, isMobile: boolean) => {

        let color: string | undefined;

        if (type == 'primary') {
            color = disabled ? theme.colors.primaryButton.disabledText : hovered ? theme.colors.primaryButton.hoverText : theme.colors.primaryButton.text;
        } else if (type == 'secondary') {
            color = disabled ? theme.colors.secondaryButton.disabledText : hovered ? theme.colors.secondaryButton.hoverText : theme.colors.secondaryButton.text;
        } else if (type == 'tertiary') {
            color = disabled ? theme.colors.neutral[300] : hovered ? theme.colors.neutral[500] : theme.colors.neutral[500];
        } else if (type == 'destructive') {
            color = disabled ? theme.colors.destructiveButton.disabledText : hovered ? theme.colors.destructiveButton.hoverText : theme.colors.destructiveButton.text;
        } else if (type == 'warning') {
            color = disabled ? theme.colors.warningButton.disabledText : hovered ? theme.colors.warningButton.hoverText : theme.colors.warningButton.text;
        } else if (type == 'destructive_secondary') {
            color = disabled ? theme.colors.destructiveSecondaryButton.disabledText : hovered ? theme.colors.destructiveSecondaryButton.hoverText : theme.colors.destructiveSecondaryButton.text;
        }

        return {
            fontWeight: FontWeight.semiBold,
            color: color,
            fontSize: isMobile ? 18 : 16,
        };
    },

    buttonIcon: (type: ButtonTypes, hovered: boolean, disabled: boolean, textStyle: TextStyle | undefined) => {
        let color: string | undefined;

        if (type == 'primary') {
            color = disabled ? theme.colors.primaryButton.disabledText : hovered ? theme.colors.primaryButton.hoverText : theme.colors.primaryButton.text;
        } else if (type == 'secondary') {
            color = disabled ? theme.colors.secondaryButton.disabledText : hovered ? theme.colors.secondaryButton.hoverText : theme.colors.secondaryButton.text;
        } else if (type == 'tertiary') {
            color = disabled ? theme.colors.neutral[300] : hovered ? theme.colors.neutral[500] : theme.colors.neutral[500];
        } else if (type == 'destructive') {
            color = disabled ? theme.colors.destructiveButton.disabledText : hovered ? theme.colors.destructiveButton.hoverText : theme.colors.destructiveButton.text;
        } else if (type == 'warning') {
            color = disabled ? theme.colors.warningButton.disabledText : hovered ? theme.colors.warningButton.hoverText : theme.colors.warningButton.text;
        } else if (type == 'destructive_secondary') {
            color = disabled ? theme.colors.destructiveSecondaryButton.disabledText : hovered ? theme.colors.destructiveSecondaryButton.hoverText : theme.colors.destructiveSecondaryButton.text;
        }

        if (textStyle?.color) {
            color = String(textStyle?.color);
        }

        return {
            color,
        };
    },

    loadingColor: {
        color: theme.colors.loadingColor
    }
}));

export default CustomButton;
